import { getUserStats } from '@db/index';
import { create } from 'zustand';
import { requireClient } from './authStore';

export type SavePayload = {
  finalScore: number;
  blockCounts: Record<string, number>;
};

export type ScoreSaveState = 'idle' | 'saving' | 'saved' | 'failed';

const PENDING_SAVE_KEY = 'pendingSavePayload';

const readPersistedPayload = (): SavePayload | null => {
  try {
    const raw = sessionStorage.getItem(PENDING_SAVE_KEY);
    return raw ? (JSON.parse(raw) as SavePayload) : null;
  } catch {
    return null;
  }
};

const createEmptyBlockCounts = (): Record<string, number> => {
  const counts: Record<string, number> = {};
  for (let i = 1; i <= 10; i++) {
    const key = `block_${String(i).padStart(2, '0')}`;
    counts[key] = 0;
  }
  return counts;
};

interface GameState {
  score: number;
  blockCounts: Record<string, number>;
  highScore: number;
  lastScore: number;
  pendingSavePayload: SavePayload | null;
  saveState: ScoreSaveState;
  savedScore: number | null;
  nextDropBlockId: number | null;
  addScore: (points: number) => void;
  incrementBlockCount: (blockId: number) => void;
  setScore: (score: number) => void;
  setNextDropBlockId: (id: number | null) => void;
  resetGame: () => void;
  saveScore: () => void;
  setPendingSave: (payload: SavePayload) => void;
  clearPendingSave: () => void;
}

export const useGameStore = create<GameState>((set, get) => ({
  score: 0,
  blockCounts: createEmptyBlockCounts(),
  highScore: parseInt(localStorage.getItem('highScore') || '0', 10),
  lastScore: parseInt(localStorage.getItem('lastScore') || '0', 10),
  pendingSavePayload: readPersistedPayload(),
  saveState: 'idle',
  savedScore: null,
  nextDropBlockId: null,

  addScore: (points: number) => {
    if (points <= 0) return;
    set((state) => ({ score: state.score + points }));
  },

  incrementBlockCount: (blockId: number) => {
    if (blockId < 1 || blockId > 10) return;
    const key = `block_${String(blockId).padStart(2, '0')}`;
    set((state) => ({
      blockCounts: {
        ...state.blockCounts,
        [key]: (state.blockCounts[key] ?? 0) + 1,
      },
    }));
  },

  setScore: (score: number) => {
    set({ score });
  },

  setNextDropBlockId: (id: number | null) => {
    set({ nextDropBlockId: id });
  },

  resetGame: () => {
    set({
      score: 0,
      blockCounts: createEmptyBlockCounts(),
      saveState: 'idle',
      savedScore: null,
      nextDropBlockId: null,
    });
  },

  saveScore: async () => {
    const { score } = get();
    localStorage.setItem('lastScore', score.toString());
    set({ lastScore: score });
    const client = requireClient();
    const {
      data: { user },
    } = await client.auth.getUser();
    const data = user?.id && (await getUserStats(client, user.id));
    if (!data) {
      return;
    }
    const { high_score } = data;
    localStorage.setItem('highScore', high_score.toString());
    if (score <= high_score) {
      return;
    }
    localStorage.setItem('highScore', score.toString());
    set({ highScore: high_score, lastScore: score });
  },

  setPendingSave: (payload: SavePayload) => {
    set({ pendingSavePayload: payload });
    try {
      sessionStorage.setItem(PENDING_SAVE_KEY, JSON.stringify(payload));
    } catch {
      /* quota exceeded or unavailable */
    }
  },

  clearPendingSave: () => {
    set({ pendingSavePayload: null });
    try {
      sessionStorage.removeItem(PENDING_SAVE_KEY);
    } catch {
      /* unavailable */
    }
  },
}));
